import { sort } from "@/services/object.js";
import { openAPIFactory } from "@/services/open-api";
import { useAppStore } from "@/stores/app.ts";
import { useFundamentalStore } from "@/stores/fundamental.ts";

/**
 * Download File
 * @param {string} fileID
 * @param {string} planID
 * @returns {AxiosPromise<string[]>}
 */
export function downloadFile(fileID, planID) {
  return openAPIFactory
    .dokumentResourceApiFactory()
    .getDokumentDatei(planID, fileID, undefined, undefined, {
      responseType: "blob",
    });
}

/**
 * Download Planarchiv
 * @param {string} planID
 * @param {string} xPlanBoxID the ID of the planarchiv in the XPlanBox
 * @returns {AxiosPromise<string[]>}
 */
export function downloadPlanarchive(planID, xPlanBoxID) {
  return openAPIFactory.verfahrenResourceApiFactory().getXPlanManagerRessource(planID, xPlanBoxID, {
    responseType: "blob",
  });
}

/**
 *  Upload Document templates
 *  @param {File} vorlage
 *  @param {string} codeMusterdokument
 *  @param {string} codeUnterlagentyp
 *  @param skipValidation
 *  @returns {AxiosPromise<VorlageRest>}
 * */
export async function uploadDocumentTemplate(
  vorlage,
  codeMusterdokument,
  codeUnterlagentyp,
  skipValidation,
) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .vorlageResourceApiFactory()
      .update(vorlage, codeMusterdokument, codeUnterlagentyp, skipValidation)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 *  Create New Document templates
 *  @param {object} payload
 *  @param {File} payload.vorlage
 *  @param {string} payload.codeMusterdokument
 *  @param {string} payload.codeVerfahrensunterlagetyp
 *  @param {string} payload.beschreibung
 *  @param {boolean} payload.skipValidation - optional
 *  @returns {AxiosPromise<VorlageRest>}
 */
export async function createNewDocumentTemplate({
  vorlage,
  codeMusterdokument,
  codeVerfahrensunterlagetyp,
  beschreibung,
  skipValidation,
}) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .vorlageResourceApiFactory()
      .create(vorlage, codeMusterdokument, codeVerfahrensunterlagetyp, beschreibung, skipValidation)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Validate Document Template by Code Mustervorlage
 * @returns {AxiosPromise<string[]>}
 * @param {string} code - Code Mustervorlage
 */
export function validateDocumentTemplate(code) {
  return new Promise((resolve, reject) => {
    return openAPIFactory
      .vorlageResourceApiFactory()
      .pruefeVorlage1(code)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Validate temporary uploaded document template
 * @returns {AxiosPromise<string[]>}
 * @param {File} file
 * @param {string} code
 * @param {string} beschreibung
 */
export function validateTemporaryUploadedDocumentTemplate(file, code, beschreibung) {
  return new Promise((resolve, reject) => {
    return openAPIFactory
      .vorlageResourceApiFactory()
      .pruefeVorlage(file, code, beschreibung)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Download Document Template
 * @param {string} codeMusterDokument
 * @param {string} version -optional
 * @returns {AxiosPromise<string[]>}
 */
export function downloadDocumentTemplate(codeMusterDokument, version = "") {
  return openAPIFactory.vorlageResourceApiFactory().getVorlageDatei(codeMusterDokument, version, {
    responseType: "blob",
  });
}

/**
 * create document from template
 * @param {object} payload
 * @param {string} payload.planID
 * @param {string} payload.codeMusterdokument
 * @param {string} payload.codeVerfahrensteilschritte (optional)
 * @return {Promise<DokumentRest>}
 */
export async function createDocumentFromTemplate({
  planID,
  codeMusterdokument,
  codeVerfahrensteilschritte,
}) {
  return new Promise((resolve, reject) => {
    if (codeVerfahrensteilschritte) {
      openAPIFactory
        .dokumentResourceApiFactory()
        .erzeugeDokumentDateiAusVorlage1(planID, codeMusterdokument, codeVerfahrensteilschritte)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    } else {
      openAPIFactory
        .dokumentResourceApiFactory()
        .erzeugeDokumentDateiAusVorlage(planID, codeMusterdokument)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    }
  });
}

/**
 * Set proceeding steps for Document template
 * @param {object} payload
 * @param {VorlageRest} payload.vorlageRest REST-Object with new proceeding steps and substeps
 * @param {string} payload.codeMusterdokument
 * @returns {Promise<VorlageRest>}
 */
export async function setProceedingStepsAndSubSteps({ vorlageRest, codeMusterdokument }) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .vorlageResourceApiFactory()
      .setVorlageVsVts(vorlageRest, codeMusterdokument)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Gets the next free codeMusterDokument
 * @returns {AxiosPromise<string>}
 */
export async function getCodeMusterDokument() {
  return openAPIFactory.vorlageResourceApiFactory().determineNextFreeCodeMusterdokument();
}

/**
 *  Delete Document template
 *  @param {string} codeMusterdokument
 *  @returns {Promise<void>}
 * */
export async function deleteDocumentTemplate(codeMusterdokument) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .vorlageResourceApiFactory()
      .deleteVorlage(codeMusterdokument)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Publish the document
 * @param {string} planID
 * @param {string} fileID
 * @returns {Promise<DokumentRest>}
 * @throws {RequiredError}
 */
export async function publishDocument(planID, fileID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .publishDokument(planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Unpublish the document
 * @param {string} planID
 * @param {string} fileID
 * @returns {Promise<DokumentRest>}
 * @throws {RequiredError}
 */
export async function unPublishDocument(planID, fileID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .unpublishDokument(planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Updates the vormerkungen for a document
 * @param {string} planID
 * @param {string} fileID
 * @param {CodeVormerkung[]} newVormerkungen Array of codeVormerkungen to update to
 * @param {CodeVormerkung[]} oldVormerkungen Array of current codeVormerkungen
 * @returns {Promise<void>}
 */
export function updateDocumentVormerkungen(planID, fileID, newVormerkungen, oldVormerkungen) {
  return new Promise((resolve, reject) => {
    const vormerkungenToAdd = newVormerkungen.filter(
      (v) => !oldVormerkungen.some((e) => e.code === v.code),
    );
    const vormerkungenToDelete = oldVormerkungen.filter(
      (v) => !newVormerkungen.some((e) => e.code === v.code),
    );
    let promisesArr = [];

    if (vormerkungenToAdd.length) {
      promisesArr.push(setDocumentVormerkungen(planID, fileID, vormerkungenToAdd));
    }

    if (vormerkungenToDelete.length) {
      promisesArr.push(deleteDocumentVormerkungen(planID, fileID, vormerkungenToDelete));
    }

    Promise.all(promisesArr)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Sets vormerkungen to the document
 * @param {string} planID
 * @param {string} fileID
 * @param {CodeVormerkung[]} vormerkungen Array of codeVormerkungen to add
 * @returns {Promise<void>}
 */
export function setDocumentVormerkungen(planID, fileID, vormerkungen) {
  return new Promise((resolve, reject) => {
    let promisesArr = [];

    vormerkungen.forEach((vormerkung) => {
      const dokumentVormerkungRest = { fileIDs: [fileID], codeVormerkung: vormerkung };

      promisesArr.push(
        openAPIFactory
          .dokumentResourceApiFactory()
          .setDokumentVormerkung(dokumentVormerkungRest, planID),
      );
    });

    Promise.all(promisesArr)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Removes vormerkungen from the document
 * @param {string} planID
 * @param {string} fileID
 * @param {CodeVormerkung[]} vormerkungen Array of codeVormerkungen to delete
 * @returns {Promise<void>}
 */
export function deleteDocumentVormerkungen(planID, fileID, vormerkungen) {
  return new Promise((resolve, reject) => {
    let promisesArr = [];

    vormerkungen.forEach((vormerkung) => {
      promisesArr.push(
        openAPIFactory
          .dokumentResourceApiFactory()
          .loescheDokumentVormerkung(planID, fileID, vormerkung.code),
      );
    });

    Promise.all(promisesArr)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Checks if document is currently published
 * @param {DokumentstatusRest[]} documentStatus
 * @returns {boolean}
 */
export function canBeDeleted(documentStatus) {
  return documentStatus.at(-1)?.dokumentstatus?.code !== "2100";
}

/**
 * Gets vormerkung names from vormerkungen array and sorts alphabetically
 * @param {VormerkungRest[]} vormerkungenArray
 * @returns {CodeVormerkung[]} sorted vormerkungen names
 */
export function getVormerkungen(vormerkungenArray) {
  const vormerkungen = vormerkungenArray.map((vormerkung) => vormerkung.vormerkung);

  return sort(vormerkungen, "name", true);
}

/**
 * Upload a new document
 * @param {object} documentInfo DokumentNeuRest
 * @param {File} file
 * @param {string} planID
 * @returns {Promise<DokumentRest>}
 */
export function uploadDocument(documentInfo, file, planID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .uploadDokument(documentInfo, file, planID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Update a document
 * @param {File} file
 * @param {string} planID
 * @param {string} fileID
 * @returns {Promise<DokumentRest>}
 */
export function updateDocument(file, planID, fileID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .updateDokumentVersion(file, planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Converts the specified file size from bytes into the largest unit possible, e.g. MB
 * @param {number} fileSize The file size in bytes
 * @returns {string} The converted file size
 */
export function getFileSize(fileSize) {
  const unit = [" bytes", " kB", " MB", " GB"];
  let step = 0;
  let output = fileSize;

  while (output > 1024 && typeof unit[step + 1] !== "undefined") {
    step++;
    output /= 1024;
  }

  return Math.round(output * 100) / 100 + unit[step];
}

/**
 * Checks if the provided file name already exists in the provided folder/VS
 * @param {string} filename
 * @param {string} planID
 * @param {string} activeVS
 * @param {string} activeFolder
 * @returns {Promise<boolean>}
 */
export async function checkIfDocumentNameAlreadyExists(filename, planID, activeVS, activeFolder) {
  let documents = [];
  const ordnername = !activeFolder && !activeVS ? "null" : activeFolder;
  const verfahrensschritt = !activeFolder && !activeVS ? "9998" : activeVS;

  await openAPIFactory
    .dokumentResourceApiFactory()
    .getDokumentUebersicht(
      planID,
      undefined,
      filename,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      ordnername,
      verfahrensschritt,
      false,
      0,
      2000,
    )
    .then((response) => {
      documents = response?.data?.content;
    });

  return documents?.find((el) => el.filename === filename);
}

/**
 * Sends a request to getDokumentUebersicht and returns the files
 * @param planID
 * @param suchBegriff
 * @param dateiNamen
 * @param bearbeiter
 * @param datum
 * @param codeDokumentstatus
 * @param versionen
 * @param codeVormerkung
 * @param codeVerfahrensunterlagetyp
 * @param fileID
 * @param klassifizierung
 * @param ordnername
 * @param codeVerfahrensschritt
 * @param papierkorb
 * @returns {Promise<unknown>} Array of files
 */
export async function getDocuments(
  planID,
  {
    suchBegriff,
    dateiNamen,
    bearbeiter,
    datum,
    codeDokumentstatus,
    versionen,
    codeVormerkung,
    codeVerfahrensunterlagetyp,
    fileID,
    klassifizierung,
    ordnername,
    codeVerfahrensschritt,
    papierkorb,
  },
) {
  const fundamentalStore = useFundamentalStore();
  let documentstatus = codeDokumentstatus;

  if (!documentstatus) {
    documentstatus = papierkorb
      ? "1800"
      : fundamentalStore
          .codelistByName("Dokumentstatus")
          .filter((status) => status.code !== "1800")
          .map((status) => status.code);
  }
  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .getDokumentUebersicht(
        planID,
        suchBegriff,
        dateiNamen,
        bearbeiter,
        datum,
        documentstatus,
        versionen,
        codeVormerkung,
        codeVerfahrensunterlagetyp,
        fileID,
        klassifizierung,
        ordnername,
        codeVerfahrensschritt,
        papierkorb,
        0,
        2000,
      )
      .then((response) => resolve(response.data?.content))
      .catch((error) => reject(error));
  });
}

/**
 * Get Externe Dienst Urls
 * @param {string} planID
 * @param {string} fileID
 * @returns {Promise<ExterneUrlsRest>}
 * @throws {RequiredError}
 */
export async function getXplanExterneUrls(planID, fileID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .xplanManagerResourceApiFactory()
      .getAllXplanExterneUrls(planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Loads details of a specific document file.
 * @param {string} fileID The document's ID.
 * @param {string} planID
 * @returns {Promise<DokumentRest>}
 */
export function getDocumentDetails(fileID, planID) {
  return new Promise((resolve, reject) => {
    if (planID === undefined) {
      resolve(false);
    }

    openAPIFactory
      .dokumentResourceApiFactory()
      .getDokumentDetail(planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Gets all XPlanGMLs.
 */
export function getAllXPlanGML() {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .xplanManagerResourceApiFactory()
      .getAllXplanGml()
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Checks the Verfahrensstand of the procedure against the Rechtsstand and PlanStatus of all published Planwerke.
 * @param {String} planID
 * @param {String} codeVerfahrensstand
 * @returns {Promise<XplanDokumentVerfahrensstandValidationRest[]>}
 */
export function getVerfahrensstandValidation(planID, codeVerfahrensstand) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .xplanManagerResourceApiFactory()
      .getPublishedXplanDokumentVerfahrensstandValidation(planID, codeVerfahrensstand)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Checks the Verfahrensstand of the procedure against the Rechtsstand and PlanStatus of a specific Planwerk.
 * @param {String} planID
 * @param {String} fileID
 * @returns {Promise<XplanDokumentVerfahrensstandValidationRest[]>}
 */
export function getSingleVerfahrensstandValidation(planID, fileID) {
  return new Promise((resolve, reject) => {
    openAPIFactory
      .xplanManagerResourceApiFactory()
      .getVerfahrensstandValidation(planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Restores a particular version of a document.
 * @param {String} planID
 * @param {String} fileID
 * @param {string} version
 * @param {string} comment
 * @returns {Promise<RechtsstandValidationRest>}
 */
export function restoreDocumentVersion(planID, fileID, version, comment) {
  const restoreRequestBody = {
    kommentar: comment,
  };

  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .versionWiederherstellen(restoreRequestBody, planID, fileID, version)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

/**
 * Returns the object with the current version of the document
 * @param {Array} versions
 * @returns {Object}
 */
export function getCurrentVersion(versions) {
  return versions.reduce((maxValue, obj) => {
    const parts1 = obj.versionsNummer.split(".").map(Number);
    const parts2 = maxValue.versionsNummer.split(".").map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 > num2) return obj;
      if (num1 < num2) return maxValue;
    }
    return maxValue;
  }, versions[0]);
}

/**
 * Returns the filename from a Content-Disposition header
 * @param {string} header
 * @param {string} extension
 * @return {string}
 * @example
 * const header = "attachment; filename*=UTF-8''example%20file.pdf";
 * "example file.pdf"
 * @example
 * const header = "attachment; filename=\"document.pdf\"";
 * "document.pdf"
 */
export function getFilenameFromContentDisposition(header, extension) {
  let filename = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);

  if (filename) {
    return decodeURIComponent(filename[1]);
  }

  filename = header.match(/filename\s*=\s*"([^"]+)"/i) || header.match(/filename\s*=\s*([^;]+)/i);
  if (filename) {
    return filename[1].trim();
  }

  return extension ? `download.${extension}` : "download";
}

export const mimeToExtension = {
  "application/pdf": "pdf",
  "application/json": "json",
  "text/csv": "csv",
  "application/xml": "xml",
  "image/png": "png",
  "image/jpeg": "jpg",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template": "dotx",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/zip": "zip",
  "application/vnd.oasis.opendocument.text-template": "ott",
  "application/vnd.oasis.opendocument.text": "odt",
};

export const extensionToMime = Object.fromEntries(
  Object.entries(mimeToExtension).map(([mime, ext]) => [ext, mime]),
);

/**
 * Returns file extension by MIME-type
 * @param {string} mimeType
 * @return {string|null}
 */
export function getExtensionByMime(mimeType) {
  return mimeToExtension[mimeType] || null;
}

/**
 * Returns MIME-type by file extension
 * @param {string} extension
 * @return {string|null}
 */
export function getMimeByExtension(extension) {
  return extensionToMime[extension] || null;
}

/**
 * Marks a document as deleted.
 * @param payload
 * @param {string} payload.planID The identifier of the proceeding.
 * @param {string} payload.fileID The identifier of the document.
 * @param {boolean} payload.hard 'false' will result in a soft delete, placing the file in the trash bucket. 'true' results in a hard/final deletion.
 * @param {boolean} payload.showPageLoadingIndicator Should a page loading indicator be displayed?
 * @param {boolean} payload.handleErrors Should the errors be handled?
 * @returns Promise<boolean>
 */
export function deleteDocument({ planID, fileID, hard, showPageLoadingIndicator, handleErrors }) {
  if (planID !== undefined) {
    const appStore = useAppStore();

    if (showPageLoadingIndicator) {
      appStore.showPageLoadingIndicator({
        id: "documentDeletionInProgress",
        text: "Einen Moment bitte, das Dokument wird gelöscht.",
      });
    }

    return new Promise((resolve, reject) => {
      openAPIFactory
        .dokumentResourceApiFactory()
        .deleteForceSingleDokumentOrSetDokumentStatusGeloescht(planID, fileID, hard.valueOf())
        .then(() => {
          resolve(true);
        })
        .catch((error) => {
          if (handleErrors) {
            appStore.showErrorModal({
              response: error,
              customErrorMessage: "Das Löschen des Dokumentes ist fehlgeschlagen!",
            });
          }

          reject(error);
        })
        .finally(() => {
          if (showPageLoadingIndicator) {
            appStore.hidePageLoadingIndicator("documentDeletionInProgress");
          }
        });
    });
  }

  return Promise.resolve(false);
}
/**
 * Shares a document with a parallel procedure.
 *
 * @param {Array<VerfahrenDokumentRest>} verfahrenDokumentRest - An array of documents to be shared with the parallel procedure.
 * @param {string} planID - The ID of the plan associated with the document.
 * @param {string} fileID - The ID of the file being shared.
 */
export function shareDokument(verfahrenDokumentRest, planID, fileID) {
  const appStore = useAppStore();

  appStore.showPageLoadingIndicator({
    id: "shareDokument",
    text: "Einen Moment, das Dokument wird mit dem Parallelverfahren geteilt.",
  });

  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .shareDokument(verfahrenDokumentRest, planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        appStore.showErrorModal({
          response: error,
          customErrorMessage: "Das Dokument konnte nicht mit dem Parallelverfahren geteilt werden.",
        });

        reject(error);
      })
      .finally(() => {
        appStore.hidePageLoadingIndicator("shareDokument");
      });
  });
}
/**
 * Updates the sharing level of a document in a parallel procedure.
 *
 * @param {Array<VerfahrenDokumentRest>} verfahrenDokumentRest - An array of documents whose sharing levels are to be updated.
 * @param {string} planID - The ID of the plan associated with the document.
 * @param {string} fileID - The ID of the file whose sharing level is being updated.
 */
export function updateSharedDokument(verfahrenDokumentRest, planID, fileID) {
  const appStore = useAppStore();

  appStore.showPageLoadingIndicator({
    id: "updateSharedDokument",
    text: "Einen Moment, die Freigabestufe für das Dokument wird aktualisiert.",
  });

  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .updateSharedDokument(verfahrenDokumentRest, planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        appStore.showErrorModal({
          response: error,
          customErrorMessage:
            "Die Freigabestufe für das Dokument konnte nicht aktualisiert werden.",
        });

        reject(error);
      })
      .finally(() => {
        appStore.hidePageLoadingIndicator("updateSharedDokument");
      });
  });
}
/**
 * Removes the sharing status of a document in a parallel procedure.
 *
 * @param {Array<VerfahrenDokumentRest>} verfahrenDokumentRest - An array of documents to be unshared.
 * @param {string} planID - The ID of the plan associated with the document.
 * @param {string} fileID - The ID of the file to be unshared.
 */
export function unshareDokument(verfahrenDokumentRest, planID, fileID) {
  const appStore = useAppStore();

  appStore.showPageLoadingIndicator({
    id: "unshareDokument",
    text: "Einen Moment, die Freigabe für das Dokument wird entfernt.",
  });

  return new Promise((resolve, reject) => {
    openAPIFactory
      .dokumentResourceApiFactory()
      .unshareDokument(verfahrenDokumentRest, planID, fileID)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        appStore.showErrorModal({
          response: error,
          customErrorMessage: "Die Freigabe für das Dokument konnte nicht entfernt werden.",
        });

        reject(error);
      })
      .finally(() => {
        appStore.hidePageLoadingIndicator("unshareDokument");
      });
  });
}
/**
 * Restores an array of documents from the deleted state.
 * @param {string} planID The identifier of the current proceeding
 * @param {string[]} files The array of fileIDs.
 * @returns {Promise<GetDokumentDetail200Response[] | false>}
 */
export function restoreDocuments(planID, files) {
  if (planID !== undefined) {
    const appStore = useAppStore();

    appStore.showPageLoadingIndicator({
      id: "restoreDocuments",
      text:
        files.length > 1
          ? "Einen Moment bitte, die Dokumente werden wiederhergestellt."
          : "Einen Moment bitte, das Dokument wird wiederhergestellt.",
    });

    return new Promise((resolve, reject) => {
      openAPIFactory
        .dokumentResourceApiFactory()
        .deleteDokumentenStatus(planID, "1800", files)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          appStore.showErrorModal({
            response: error,
            customErrorMessage:
              files.length > 1
                ? "Das Wiederherstellen der Dokumente ist fehlgeschlagen!"
                : "Das Wiederherstellen des Dokuments ist fehlgeschlagen!",
          });

          reject(error);
        })
        .finally(() => {
          appStore.hidePageLoadingIndicator("restoreDocuments");
        });
    });
  }

  return Promise.resolve(false);
}
/**
 * Deletes an array of documents.
 * @param {string} planID The ID of the proceeding.
 * @param {string[]} files The array of fileIDs.
 * @returns {Promise<boolean[]>}
 */
export function deleteDocuments(planID, files) {
  const appStore = useAppStore();

  appStore.showPageLoadingIndicator({
    id: "deleteDocuments",
    text:
      files.length > 1
        ? "Einen Moment bitte, die Dokumente werden gelöscht."
        : "Einen Moment bitte, das Dokument wird gelöscht.",
  });

  return new Promise((resolve, reject) => {
    const promises = [];

    for (const file of files) {
      promises.push(
        deleteDocument({
          planID: planID,
          fileID: file,
          hard: true,
          showPageLoadingIndicator: false,
          handleErrors: false,
        }),
      );
    }

    const resolved = [];
    const rejected = [];

    Promise.allSettled(promises).then((results) => {
      for (const result of results) {
        if (result.status === "rejected") {
          rejected.push(result.reason);
        } else {
          resolved.push(result.value);
        }
      }

      if (resolved.length < files.length) {
        const errors = [];

        rejected.forEach((error, i) => {
          errors.push("(" + (i + 1) + ") " + error);
        });

        appStore.showErrorModal({
          response: {
            data: {
              message: errors.join(" "),
            },
          },
          customErrorMessage:
            files.length > 1
              ? "Das Löschen der Dokumente ist fehlgeschlagen!"
              : "Das Löschen des Dokuments ist fehlgeschlagen!",
        });

        reject(errors.join(" "));
      } else {
        resolve(resolved);
      }

      appStore.hidePageLoadingIndicator("deleteDocuments");
    });
  });
}

/**
 * Removes a marking of a specific document.
 * @param payload
 * @param {string} payload.planID The identifier of the proceeding.
 * @param {string} payload.fileID The file ID.
 * @param {string} payload.markingCode The marking code to remove.
 * @returns {Promise<DokumentRest | false>}
 */
export function deleteDocumentMarking({ planID, fileID, markingCode }) {
  if (planID !== undefined) {
    const fundamentalStore = useFundamentalStore();
    const appStore = useAppStore();

    const vormerkungToBeRemoved = fundamentalStore
      .codelistByName("Vormerkungen")
      .find((vormerkung) => vormerkung.code === markingCode);

    return new Promise((resolve, reject) => {
      openAPIFactory
        .dokumentResourceApiFactory()
        .loescheDokumentVormerkung(planID, fileID, markingCode)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          appStore.showErrorModal({
            response: error,
            customErrorMessage:
              "Die Löschung der Vormerkung" +
              (vormerkungToBeRemoved?.name ? ' "' + vormerkungToBeRemoved.name + '"' : "") +
              ' (Code: "' +
              markingCode +
              '") ist fehlgeschlagen!',
          });

          reject(error);
        });
    });
  }

  return Promise.resolve(false);
}
